import axios from "axios";
import { compareAsc, format, parse } from "date-fns/esm";

import { auth } from "@/auth";
import { Item } from "@/tools/msa/models/item";
import { Document, Session } from "@/tools/msa/models";
import { Response } from "@/tools/msa/models/requests/snomed";
import store from "@/store/root";

const CUSTOM_TERMS = [
  "TENS unit",
  "Transcutaneous unit",
  "Electrical stim unit",
  "Electrical stimulation unit",
  "Neuro stimulator",
  "Neuro stimulator unit",
  "Interferrential unit",
  "IFR unit",
  "RFA",
  "Radio frequency ablation",
  "Rhizotomy",
  "Surgery",
  "Spinal Cord Stimulator",
  "SCS",
  "Spinal Stim",
  "Nevro Unit",
  "H10 Unit",
  "H10 Therapy",
  "10HF Stimulator",
  "Novel Stimulator",
  "High Frequency 10 Therapy",
  "Medtronic",
  "Intrathecal Pain Pump",
  "ITP",
  "ITPP",
  "Intrathecal Catheter",
  "Pump",
  "Fusion",
  "Fusion Surgery",
  "Fusion Revision",
  "Hardware Removal",
  "Arthroplasty",
  "Repair",
  "Revision",
  "Replacement",
  "RCR",
  "Wean",
  "Boston scientific",
  "Nevro",
  "Dorsal Column Stimulator",
  "Spinal Cord Stimulation",
  "Stimulation",
  "Intellis",
  "Senza",
  "HF10",
  "IPG",
  "Evolve",
  "ITDD",
  "Intrathecal drug delivery system",
  "Arthroscopy",
  "spinal stimulation",
  "Dorsal Stimulator",
  "DCS",
  "Ketamine",
  "Microprocessor",
  "C-leg",
  "Ossur",
  "Ottobock",
  "Otto Bock",
  "Amputation",
  "AKA",
  "BKA",
  "Pro-Knee",
  "Proprio",
  "Rheo",
  "NESS",
  "Myo-electric",
  "Myoelectric",
  "Blatchford",
  "Endolite",
  "Myo-Bock",
  "Myobock",
  "PowerKnee",
  "PENS",
  "Peripheral nerve stimulator",
  "stimulator",
  "Intellis",
  "Intellus"
];

export const encodeDocumentWithCustomTerms = async (
  document: Document,
  session: Session
): Promise<Document> => {
  const sections = document.pages.flatMap(page => page.sections);

  await axios
    .post<Response>(
      (store.state as any).settings.current.endpoints.customEncode,
      {
        document: {
          dob: session.patient.dateOfBirth
            ? format(session.patient.dateOfBirth, "y-M-d")
            : "",
          pages: sections.map(section => ({
            date:
              section.page.date || document.date
                ? format(
                    parse(
                      (section.page.date || document.date)!,
                      "M/d/y",
                      new Date()
                    ),
                    "y-M-d"
                  )
                : "",
            text: section.tokens
              .map(token => `${token.text}${token.delimiter}`)
              .join(""),
            action:
              section.included === null
                ? "null"
                : section.included
                ? "include"
                : "exclude"
          }))
        },
        queries: CUSTOM_TERMS
      },
      { headers: await auth.headers() }
    )
    .then(response => response.data)
    .then(data => {
      sections.forEach(
        (section, index) =>
          (section.included = !data.pages_excluded.includes(index + 1))
      );
      const tokens = sections
        .filter(section => section.included)
        .flatMap(section => section.tokens);

      document.items = [
        ...document.items.filter(item => item.type !== "custom_match"),
        ...data.icd.imatches
          .filter(match => Object.keys(match).length > 0)
          .map(
            (match): Item => ({
              type: "custom_match",
              document,
              match: {
                coding: Object.freeze({
                  type: match.codetype,
                  system: match.codeset,
                  code: match.code,
                  display: match.codename
                }),
                refers: {
                  dates: match.refers.dates
                    .filter(Boolean)
                    .filter(date =>
                      /[0-9]{1,2}-[0-9]{1,2}-[0-9]{1,4}/.test(date)
                    )
                    .map(date => parse(date, "y-M-d", new Date()))
                    .sort(compareAsc)
                    .map(date => format(date, "M/d/y"))
                },
                locations: match.tokspans
                  .filter(span => span.length === 2)
                  .map(span => tokens.slice(...span))
              }
            })
          )
      ];
    });

  return document;
};
